import React, { useEffect, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Sparkles, Send, Loader2, RotateCcw, Bot } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import MyEventSidebar from "./MyEventSidebar";

const QUICK_SUGGESTIONS = [
  "Que dois-je faire prochainement ?",
  "Quel est mon prochain rendez-vous ?",
  "Combien reste-t-il à payer ?",
  "Combien d'invités sont confirmés ?",
  "Quels sont mes morceaux importants ?",
];

const GREETING = {
  role: "assistant",
  content:
    "Bonjour ! Je suis MyAssistant, votre assistant privé pour ce mariage. Posez-moi vos questions sur votre dossier — planning, invités, prestataires, budget, playlist… Je réponds uniquement à partir de vos données.",
  at: null,
};

export default function MyEventAssistant() {
  const { eventId } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [quotaLeft, setQuotaLeft] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!eventId) return;
    api
      .get(`/next/myevent/${eventId}/assistant/history`)
      .then((r) => {
        const msgs = r.data?.messages || [];
        const cap = r.data?.quota_daily_cap;
        if (cap) setQuotaLeft(cap);
        setMessages(msgs.length ? [GREETING, ...msgs] : [GREETING]);
      })
      .catch((e) => setErr(e?.response?.status || 500));
  }, [eventId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  if (!user) return <Navigate to="/connexion" replace />;
  if (err === 403) return <Err title="Accès refusé" text="Vous n'êtes pas membre de cet événement." testid="assistant-forbidden" />;
  if (err === 404) return <Err title="Événement introuvable" text="Cet événement n'existe pas." testid="assistant-notfound" />;

  const send = async (text) => {
    const q = (text || "").trim();
    if (!q || sending) return;
    setMessages((m) => [...m, { role: "user", content: q, at: new Date().toISOString() }]);
    setInput("");
    setSending(true);
    try {
      const r = await api.post(`/next/myevent/${eventId}/assistant/chat`, { message: q });
      setMessages((m) => [...m, { role: "assistant", content: r.data.reply || "…", at: new Date().toISOString() }]);
      if (typeof r.data?.quota_used_today === "number" && typeof r.data?.quota_daily_cap === "number") {
        setQuotaLeft(Math.max(r.data.quota_daily_cap - r.data.quota_used_today, 0));
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 429) {
        toast.error(detail || "Trop de messages — patientez un instant.");
      } else {
        setMessages((m) => [...m, {
          role: "assistant",
          content: detail || "Désolé, une erreur est survenue. Réessayez dans un instant.",
          at: new Date().toISOString(),
        }]);
      }
    } finally {
      setSending(false);
    }
  };

  const reset = async () => {
    try {
      await api.delete(`/next/myevent/${eventId}/assistant/history`);
      setMessages([GREETING]);
      toast.success("Conversation réinitialisée.");
    } catch { toast.error("Impossible de réinitialiser."); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send(input);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-20" data-testid="myevent-assistant">
      <header className="border-b border-[#DCB457]/15 bg-black/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-4 flex items-center justify-between">
          <Link to={`/myevent/${eventId}`} data-testid="assistant-back" className="text-xs uppercase tracking-widest text-white/60 hover:text-[#DCB457] inline-flex items-center gap-2">
            <ArrowLeft size={13} /> MyEvent
          </Link>
          <span className="text-[10px] uppercase tracking-[0.24em] text-[#DCB457] border border-[#DCB457]/40 px-2 py-1">MyAssistant</span>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-8 lg:pt-12 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <MyEventSidebar />
        <main className="lg:col-span-9 min-w-0 space-y-6">
          <section>
            <p className="eyebrow">MyEvent · Module</p>
            <h1 className="font-serif-luxe text-4xl md:text-5xl mt-3">
              Votre <span className="italic gold-text">assistant mariage.</span>
            </h1>
            <p className="mt-3 text-white/60 text-sm md:text-base">
              Toujours à vos côtés — réponses instantanées à partir de votre dossier.
            </p>
            {quotaLeft != null && quotaLeft <= 5 && (
              <p className="mt-2 text-[11px] text-amber-300" data-testid="assistant-quota">
                {quotaLeft} message{quotaLeft > 1 ? "s" : ""} restant{quotaLeft > 1 ? "s" : ""} aujourd'hui.
              </p>
            )}
          </section>

          {/* Chat panel */}
          <section className="card-luxe overflow-hidden flex flex-col" style={{ minHeight: "520px" }}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#DCB457]/15">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full border border-[#DCB457]/50 flex items-center justify-center text-[#DCB457]">
                  <Sparkles size={16} strokeWidth={1.3} />
                </div>
                <div>
                  <p className="font-serif-luxe text-base text-white leading-none">MyAssistant</p>
                  <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/80 mt-1 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 bg-emerald-400 rounded-full" /> Contexte privé
                  </p>
                </div>
              </div>
              <button
                onClick={reset}
                data-testid="assistant-reset"
                className="text-[10px] uppercase tracking-widest text-white/50 hover:text-[#DCB457] inline-flex items-center gap-1.5"
              >
                <RotateCcw size={11} /> Nouvelle conversation
              </button>
            </div>

            <div
              ref={scrollRef}
              className="flex-1 overflow-y-auto px-4 md:px-6 py-5 space-y-3"
              data-testid="assistant-messages"
              style={{ maxHeight: "60vh" }}
            >
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`assistant-msg-${m.role}-${i}`}
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-[#DCB457] text-black"
                        : "bg-black/40 border border-[#DCB457]/15 text-white/90"
                    }`}
                    style={{ borderRadius: m.role === "user" ? "12px 12px 2px 12px" : "12px 12px 12px 2px" }}
                  >
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start" data-testid="assistant-loading">
                  <div className="bg-black/40 border border-[#DCB457]/15 px-4 py-2.5 inline-flex items-center gap-2 text-white/70 text-sm rounded-xl">
                    <Loader2 size={14} className="animate-spin text-[#DCB457]" />
                    MyAssistant réfléchit…
                  </div>
                </div>
              )}
            </div>

            {/* Quick suggestions */}
            {messages.length <= 1 && !sending && (
              <div className="px-4 md:px-6 py-3 border-t border-[#DCB457]/10 flex flex-wrap gap-2" data-testid="assistant-suggestions">
                {QUICK_SUGGESTIONS.map((s, idx) => (
                  <button
                    key={idx}
                    onClick={() => send(s)}
                    data-testid={`assistant-suggestion-${idx}`}
                    className="text-[11px] px-3 py-1.5 border border-[#DCB457]/30 text-[#DCB457]/90 hover:bg-[#DCB457]/10 hover:border-[#DCB457]/60 transition"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <form
              onSubmit={(e) => { e.preventDefault(); send(input); }}
              className="border-t border-[#DCB457]/15 p-3 md:p-4 flex items-end gap-2"
            >
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder="Posez votre question…"
                rows={1}
                maxLength={800}
                data-testid="assistant-input"
                className="flex-1 bg-black/60 border border-[#DCB457]/25 px-3 py-2 text-sm text-white focus:border-[#DCB457] focus:outline-none resize-none max-h-32"
              />
              <button
                type="submit"
                disabled={!input.trim() || sending}
                data-testid="assistant-send"
                className="shrink-0 w-11 h-11 bg-[#DCB457] text-black hover:bg-[#e6c454] transition disabled:opacity-40 flex items-center justify-center"
                aria-label="Envoyer"
              >
                <Send size={16} strokeWidth={1.5} />
              </button>
            </form>
          </section>

          <p className="text-[10px] uppercase tracking-widest text-white/30 italic" data-testid="assistant-disclaimer">
            MyAssistant utilise uniquement les informations de votre dossier. Pour toute modification, contactez My Animation.
          </p>
        </main>
      </div>
    </div>
  );
}

function Err({ title, text, testid }) {
  return (
    <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
      <div className="max-w-md text-center" data-testid={testid}>
        <p className="eyebrow flex items-center justify-center gap-2"><Bot size={12} /> MyAssistant</p>
        <h1 className="font-serif-luxe text-3xl mt-3 mb-3">{title}</h1>
        <p className="text-white/60 text-sm">{text}</p>
      </div>
    </div>
  );
}
